/**
 * Split-attention arithmetic — "what is missing before this animal can be
 * processed": a split layout, a buyer in every slot, a cut sheet per slot.
 *
 * Shared because two sides count the same thing from different shapes. The
 * producer dashboard counts it in the browser off `job.animalHeads` (the
 * GraphQL façade, splits already embedded); the API counts it across a
 * producer's whole book off `Animal` + `Split` documents, for the action-card
 * totals — which have to agree with the badges on the cards below them, or the
 * dashboard is lying. Each side maps its own shape into {@link AttentionHead}.
 */

/**
 * Slots a layout is expected to fill. An animal whose `splitType` is unknown
 * to this table is treated as a single slot, which is the conservative read —
 * it can under-count missing buyers but never invents them.
 */
export const EXPECTED_SLOT_COUNT: Record<string, number> = {
  whole: 1,
  half: 2,
  quarters: 4,
  half_and_two_quarters: 3,
};

export type AttentionSplit = {
  /** Assigned buyer, if any. */
  buyer?: { _id?: string | null } | null;
  /** V1 cut sheet. */
  cutsheet?: { _id?: string | null } | null;
  /** V2 questionnaire response. Either one satisfies the cut-sheet check. */
  questionnaireResponse?: { _id?: string | null } | null;
};

export type AttentionHead = {
  /** Derived layout. Absent means the producer hasn't chosen one yet. */
  splitType?: string | null;
  splits?: Array<AttentionSplit | null> | null;
} | null;

export type AttentionCounts = {
  missingSplits: number;
  missingBuyers: number;
  missingCutsheets: number;
};

/**
 * Slots on this head with nobody assigned: slots the layout calls for that
 * don't exist yet, plus existing splits with no buyer.
 * @param head - The animal head to count.
 * @returns Number of unassigned slots.
 */
export function countMissingBuyers(head: AttentionHead): number {
  if (!head?.splitType) return 0;
  const splits = head.splits ?? [];
  const expected = EXPECTED_SLOT_COUNT[head.splitType] ?? 1;

  // Slots not yet created count as missing buyers.
  const missingSlots = Math.max(0, expected - splits.length);
  const missingInSplits = splits.filter((s) => !s?.buyer).length;

  return missingSlots + missingInSplits;
}

/**
 * Splits on this head with neither a V1 cut sheet nor a V2 questionnaire
 * response. Heads with no layout yet are skipped — the missing layout is the
 * issue to report, not the cut sheets it implies.
 * @param head - The animal head to count.
 * @returns Number of splits still needing a cut sheet.
 */
export function countMissingCutsheets(head: AttentionHead): number {
  if (!head?.splitType) return 0;
  return (head.splits ?? []).filter(
    (s) => !s?.cutsheet && !s?.questionnaireResponse?._id,
  ).length;
}

/**
 * Splits on this head that HAVE a buyer but neither cut sheet form — the cut
 * sheets somebody actually owes. A slot with nobody on it has no cut sheet
 * either, but that is the missing buyer showing through, not a second issue:
 * {@link countMissingCutsheets} counts it (the card badge's arithmetic), the
 * "Missing Cut Sheets" job filter must not, or it lists every job that is
 * only short a buyer (PRO-805).
 * @param head - The animal head to count.
 * @returns Number of assigned splits still needing a cut sheet.
 */
export function countAssignedSplitsMissingCutsheets(
  head: AttentionHead,
): number {
  if (!head?.splitType) return 0;
  return (head.splits ?? []).filter(
    (s) => !!s?.buyer && !s.cutsheet && !s.questionnaireResponse?._id,
  ).length;
}

/**
 * Roll the three counts up across a set of heads.
 * @param heads - The animal heads to count over.
 * @returns The three per-category totals.
 */
export function countAttention(
  heads: ReadonlyArray<AttentionHead> | null | undefined,
): AttentionCounts {
  if (!heads?.length) {
    return { missingSplits: 0, missingBuyers: 0, missingCutsheets: 0 };
  }

  return {
    missingSplits: heads.filter((h) => !h?.splitType).length,
    missingBuyers: heads.reduce((sum, h) => sum + countMissingBuyers(h), 0),
    missingCutsheets: heads.reduce(
      (sum, h) => sum + countMissingCutsheets(h),
      0,
    ),
  };
}
